import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@risk-first/ui-core";
import { DailyValuationKickoffRequest } from "../../store/types";
import {
  Overlay,
  Dialog,
  Title,
  Description,
  FieldGroup,
  CheckboxLabel,
  DateFieldLabel,
  DateInput,
  Actions,
} from "./DailyValuationKickoffDialog.style";

export interface DailyValuationKickoffDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (request: DailyValuationKickoffRequest) => void;
}

/**
 * Manual Daily Valuation Kickoff popup (PMDEV-2198). Lets support select any combination of the
 * three calculation types (Liability / Asset / Metrics) plus an optional run-date override, then
 * publishes them via the Cozonac API. Confirm is disabled until at least one type is selected.
 */
export const DailyValuationKickoffDialog = ({
  isOpen,
  onClose,
  onConfirm,
}: DailyValuationKickoffDialogProps): JSX.Element | null => {
  const [liability, setLiability] = useState(false);
  const [asset, setAsset] = useState(false);
  const [metrics, setMetrics] = useState(false);
  const [runUpUntil, setRunUpUntil] = useState("");
  const firstCheckboxRef = useRef<HTMLInputElement>(null);

  const reset = useCallback(() => {
    setLiability(false);
    setAsset(false);
    setMetrics(false);
    setRunUpUntil("");
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  // When open: move focus into the dialog and close on Escape (keyboard accessibility).
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    firstCheckboxRef.current?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, handleClose]);

  if (!isOpen) {
    return null;
  }

  const atLeastOneSelected = liability || asset || metrics;

  const handleConfirm = () => {
    if (!atLeastOneSelected) {
      return;
    }

    onConfirm({
      liability,
      asset,
      metrics,
      runUpUntil: runUpUntil ? runUpUntil : null,
    });

    reset();
  };

  return (
    <Overlay data-testid="kickoff-dialog-overlay">
      <Dialog role="dialog" aria-modal="true" aria-label="Manual daily valuation kickoff">
        <Title>Manual Rerun</Title>
        <Description>
          Select which calculation types to retrigger. An optional date override applies to all
          selected types.
        </Description>

        <FieldGroup>
          <CheckboxLabel>
            <input
              ref={firstCheckboxRef}
              type="checkbox"
              data-testid="kickoff-liability"
              checked={liability}
              onChange={(e) => setLiability(e.target.checked)}
            />
            Liability
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              data-testid="kickoff-asset"
              checked={asset}
              onChange={(e) => setAsset(e.target.checked)}
            />
            Asset
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              data-testid="kickoff-metrics"
              checked={metrics}
              onChange={(e) => setMetrics(e.target.checked)}
            />
            Metrics
          </CheckboxLabel>
        </FieldGroup>

        <DateFieldLabel>
          Date override (optional)
          <DateInput
            type="date"
            data-testid="kickoff-date"
            value={runUpUntil}
            onChange={(e) => setRunUpUntil(e.target.value)}
          />
        </DateFieldLabel>

        <Actions>
          <Button size="small" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="primary"
            data-testid="kickoff-confirm"
            disabled={!atLeastOneSelected}
            onClick={handleConfirm}
          >
            Trigger
          </Button>
        </Actions>
      </Dialog>
    </Overlay>
  );
};
